import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  tIntercomConfig,
  tIntercomMethods,
  tUseIntercom,
} from './Intercom.types';
import { useUserContext } from '../../providers/UserProvider/User.context';
import { useWorkspaceContext } from '../../providers/WorkspaceProvider/Workspace.context';
import { useSubscriptionContext } from '../../providers/SubscriptionProvider/Subscription.context';
import { useIntegrationsContext } from '../../providers/IntegrationsProvider/Integrations.context';
import { eIntegrationStatus } from '../../providers/IntegrationsProvider/Integrations.enums';
import {
  formatUserRole,
  mapSubscriptionStatus,
  getPlanName,
  extractEmailDomain,
  centsToDollars,
  countWorkspaceUsers,
  dateToLocaleDateString,
  calculateDaysUntilSubscriptionEnd,
} from './Intercom.functions';
import { LeadbotService } from 'src/pages/LeadBot/LeadBot.services';
import { useACLContext } from 'src/providers/ACLProvider/ACL.hook';
import {
  ePlanCode,
  eSubscriptionStatus,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { isUndefined } from 'lodash';
import { useIntercomJWT } from './useIntercomJWT.hook';
import { firebase } from '../../firebase/firebase.singleton';
import { IS_PRODUCTION } from 'src/constants/envs';

// Declare global Intercom function
declare global {
  interface Window {
    Intercom: (method: string, ...args: unknown[]) => unknown;
    intercomSettings: tIntercomConfig;
  }
}

/**
 * @module hooks/Intercom
 * Hook that integrates Intercom messenger into the application.
 *
 * @description This hook provides a complete interface to interact with Intercom,
 * including initialization, user management, and messenger controls.
 * It dynamically loads the Intercom script and provides methods to control the widget.
 *
 * @param {string} appId - The Intercom application ID
 * @param {boolean} autoInitialize - Whether to automatically initialize Intercom on mount
 * @returns {tUseIntercom} Object containing Intercom state and methods
 *
 * @example
 * // In a React component
 * function MyComponent() {
 *   const intercom = useIntercom('your-app-id', true);
 *
 *   const handleShowMessages = () => {
 *     intercom.methods.showMessages();
 *   };
 *
 *   return <button onClick={handleShowMessages}>Show Messages</button>;
 * }
 */
export default function useIntercom(
  appId: string,
  autoInitialize = true,
): tUseIntercom {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [leadbotStatus, setLeadbotStatus] = useState(false);
  // INT-1500 U3: booted or updated with the user's id and JWT, not anonymous.
  const [isIdentified, setIsIdentified] = useState(false);
  const hasBootedRef = useRef(false);

  // Check if Intercom is active only in production
  const isActiveIntercomProduction = IS_PRODUCTION;

  // Get current location for route tracking
  const location = useLocation();

  // Get context data for metadata
  const { currentUser, currentUserRole } = useUserContext();
  const { currentWorkspace, workspaceInfo } = useWorkspaceContext();
  const { subscription } = useSubscriptionContext();
  const {
    whatsappIntegration,
    facebookIntegration,
    instagramIntegration,
    whatsappBusinessIntegration,
  } = useIntegrationsContext();
  const {
    templateCreditsLimit,
    leadbotLevelsAccess,
    apiKeyAccess,
    watermarkRemoval,
    activeLeadsCount,
    isTrial,
  } = useACLContext();

  // Get Intercom JWT
  const { intercomJWT, isLoading: isJWTLoading } = useIntercomJWT();

  // Track Firebase UID reactively
  const [firebaseUID, setFirebaseUID] = useState<string | undefined>(
    firebase.auth.currentUser?.uid,
  );

  // Update Firebase UID when auth state changes
  useEffect(() => {
    const unsubscribe = firebase.auth.onAuthStateChanged((user) => {
      setFirebaseUID(user?.uid);

      // If user logged out, shutdown Intercom
      if (!user && typeof window.Intercom !== 'undefined') {
        window.Intercom('shutdown');
        hasBootedRef.current = false;
        setIsIdentified(false);
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  /**
   * Get leadbot status
   */
  async function getLeadbotStatus() {
    const response = await LeadbotService.getLeadbotStatus(
      currentWorkspace?.id || '',
    );
    setLeadbotStatus(response?.data?.isChatbotActive);
  }

  useEffect(() => {
    if (currentWorkspace?.id) {
      getLeadbotStatus();
    }
  }, [currentWorkspace?.id]);

  /**
   * Boot Intercom with user configuration
   */
  const boot = useCallback((config: tIntercomConfig) => {
    if (window.Intercom) {
      window.Intercom('boot', config);
    }
  }, []);

  /**
   * Shutdown Intercom session
   */
  const shutdown = useCallback(() => {
    if (window.Intercom) {
      window.Intercom('shutdown');
    }
  }, []);

  /**
   * Update user information
   */
  const update = useCallback((config?: Partial<tIntercomConfig>) => {
    if (window.Intercom) {
      window.Intercom('update', config);
    }
  }, []);

  /**
   * Hide the Intercom messenger
   */
  const hide = useCallback(() => {
    if (window.Intercom) {
      window.Intercom('hide');
    }
  }, []);

  /**
   * Show the Intercom messenger
   */
  const show = useCallback(() => {
    if (window.Intercom) {
      window.Intercom('show');
    }
  }, []);

  /**
   * Show the message list
   */
  const showMessages = useCallback(() => {
    if (window.Intercom) {
      window.Intercom('showMessages');
    }
  }, []);

  /**
   * Show new message composer
   */
  const showNewMessage = useCallback((prePopulatedContent?: string) => {
    if (window.Intercom) {
      window.Intercom('showNewMessage', prePopulatedContent);
    }
  }, []);

  /**
   * Send a message as a new conversation in one step (INT-1500 D17)
   */
  const startConversation = useCallback((message: string) => {
    if (window.Intercom) {
      window.Intercom('startConversation', message);
    }
  }, []);

  /**
   * Track custom events
   */
  const trackEvent = useCallback(
    (
      eventName: string,
      metadata?: Record<string, string | number | boolean>,
    ) => {
      if (window.Intercom) {
        window.Intercom('trackEvent', eventName, metadata);
      }
    },
    [],
  );

  /**
   * Get visitor ID
   */
  const getVisitorId = useCallback((): string => {
    if (window.Intercom) {
      return window.Intercom('getVisitorId') as string;
    }
    return '';
  }, []);

  /**
   * Start a product tour
   */
  const startTour = useCallback((tourId: number) => {
    if (window.Intercom) {
      window.Intercom('startTour', tourId);
    }
  }, []);

  /**
   * Register callback for when messenger is hidden
   */
  const onHide = useCallback((callback: () => void) => {
    if (window.Intercom) {
      window.Intercom('onHide', callback);
    }
  }, []);

  /**
   * Register callback for when messenger is shown
   */
  const onShow = useCallback((callback: () => void) => {
    if (window.Intercom) {
      window.Intercom('onShow', callback);
    }
  }, []);

  /**
   * Register callback for unread count changes
   */
  const onUnreadCountChanged = useCallback(
    (callback: (unreadCount: number) => void) => {
      if (window.Intercom) {
        window.Intercom('onUnreadCountChanged', callback);
      }
    },
    [],
  );

  /*
   * Function to check if the user has the bolsa recurrente feature
   */
  const isRecurringFund = () => {
    if (
      subscription.planCode === ePlanCode.PLAN_PROFESSIONAL &&
      templateCreditsLimit > 250
    ) {
      return true;
    }
    if (
      subscription.planCode === ePlanCode.PLAN_ENTERPRISE &&
      templateCreditsLimit > 500
    ) {
      return true;
    }
    return false;
  };

  /**
   * Load Intercom script dynamically
   */
  const loadIntercom = useCallback(() => {
    if (
      typeof window.Intercom !== 'undefined' ||
      document.getElementById('intercom-script')
    ) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.id = 'intercom-script';
    script.async = true;
    script.src = `https://widget.intercom.io/widget/${appId}`;

    script.onload = () => {
      setIsLoaded(true);
    };

    script.onerror = () => {
      console.error('Failed to load Intercom script');
    };

    document.head.appendChild(script);
  }, [appId]);

  /**
   * Build complete Intercom configuration with metadata
   */
  const intercomConfig = useMemo((): tIntercomConfig | null => {
    // Anonymous mode: boot with only app_id if user is not loaded
    if (!currentUser || !currentWorkspace || !intercomJWT) {
      const config: tIntercomConfig = {
        app_id: appId,
      };
      return config;
    }

    // Build contact-level configuration
    // Use Firebase UID for user_id (must match the UID used in backend path)
    const config: tIntercomConfig = {
      app_id: appId,
      user_id: firebaseUID || currentUser.authId,
      intercom_user_jwt: intercomJWT || undefined,
      name: currentUser.name,
      email: currentUser.email,
      Rol: currentUserRole ? formatUserRole(currentUserRole) : undefined,
      WID: currentWorkspace.id,
      workspaceName: currentWorkspace.name,
      Industry: workspaceInfo?.industryType,
      phone: currentUser.phoneNumber?.toString(),
      stripe_cus_id: subscription.customerId,
      email_domain: extractEmailDomain(currentUser.email),
      'Allowed Users': subscription.purchasedSeats + subscription.includedSeats,
      'WID Creation Date': dateToLocaleDateString(workspaceInfo?.createdAt),
      'Registered Users': countWorkspaceUsers(workspaceInfo?.users ?? 0),
      'Subscription Date': dateToLocaleDateString(subscription?.startDate),
      'Next Invoice Payment Due': dateToLocaleDateString(subscription.endDate),
      Trial: isTrial,
      'Trial Start Date':
        !isUndefined(subscription.trialDays) && isTrial
          ? dateToLocaleDateString(subscription.startDate)
          : undefined,
      'Days left in Trial': subscription?.trialDays,
      'Subscription Status': mapSubscriptionStatus(subscription.status),
      'Suscription Plan': subscription.planCode
        ? getPlanName(subscription.planCode)
        : 'Unknown',
      'WhatsApp Web Connected':
        whatsappIntegration.state === eIntegrationStatus.CONNECTED,
      'WAPI Connected':
        whatsappBusinessIntegration.state === eIntegrationStatus.CONNECTED,
      'Instagram Connected':
        instagramIntegration.state === eIntegrationStatus.CONNECTED,
      'Facebook Connected':
        facebookIntegration.state === eIntegrationStatus.CONNECTED,
      'Leadbot active': leadbotStatus,
      'Marketing Templates Available': templateCreditsLimit,
      'Utility Templates Available': activeLeadsCount,
      'Leadbot Premium': leadbotLevelsAccess,
      API: apiKeyAccess,
      'Bolsa Recurrente': isRecurringFund(),
      'Marca de agua': watermarkRemoval,

      // Company data
      company: {
        id: currentWorkspace.id,
        name: currentWorkspace.name,
        created_at: dateToLocaleDateString(workspaceInfo?.createdAt),
        plan: subscription.planCode
          ? getPlanName(subscription.planCode)
          : 'Unknown',
        monthly_spend: centsToDollars(subscription.totalAmount),
        subscription_status: mapSubscriptionStatus(subscription.status),
        active_users: subscription.purchasedSeats + subscription.includedSeats,
        registered_users: countWorkspaceUsers(workspaceInfo?.users ?? 0),
        'Allowed Users':
          subscription.purchasedSeats + subscription.includedSeats,
        pending_cancellation:
          subscription.pendingCancellation ||
          subscription.status === eSubscriptionStatus.SCHEDULED_CANCELLATION,
        cancellation_date:
          subscription.pendingCancellation ||
          subscription.status === eSubscriptionStatus.SCHEDULED_CANCELLATION
            ? dateToLocaleDateString(new Date())
            : undefined,
        days_until_subscription_end: calculateDaysUntilSubscriptionEnd(
          subscription.endDate,
        ),
        stripe_customer_id: subscription?.customerId,
        website: undefined, // TODO: Not available in workspace data
        industry: workspaceInfo?.industryType,
        whatsapp_web_connected:
          whatsappIntegration.state === eIntegrationStatus.CONNECTED,
        wapi_connected:
          whatsappBusinessIntegration.state === eIntegrationStatus.CONNECTED,
        instagram_connected:
          instagramIntegration.state === eIntegrationStatus.CONNECTED,
        facebook_connected:
          facebookIntegration.state === eIntegrationStatus.CONNECTED,
        Active_leads: activeLeadsCount,
        leads_total: Number(localStorage.getItem('totalLeads')) || 0,
      },
    };

    return config;
  }, [
    appId,
    location.pathname,
    currentUser,
    currentUserRole,
    currentWorkspace,
    workspaceInfo,
    subscription,
    whatsappIntegration,
    facebookIntegration,
    instagramIntegration,
    whatsappBusinessIntegration,
    firebaseUID,
    intercomJWT,
  ]);

  /**
   * Initialize Intercom with complete metadata
   */
  const initialize = useCallback(() => {
    if (!isLoaded || intercomConfig === null) return;

    // If user is authenticated and we have JWT, boot with user_id and JWT
    // Otherwise, boot in anonymous mode (only app_id)
    if (currentUser && currentWorkspace && firebaseUID && intercomJWT) {
      boot({
        ...intercomConfig,
        user_id: firebaseUID,
        intercom_user_jwt: intercomJWT,
      });
      hasBootedRef.current = true;
      setIsIdentified(true);
    } else {
      // Anonymous mode: boot without user_id
      boot({
        app_id: appId,
      });
      hasBootedRef.current = true;
      setIsIdentified(false);
    }

    // Set up event listeners
    onShow(() => setIsVisible(true));
    onHide(() => setIsVisible(false));
    onUnreadCountChanged((count: number) => setUnreadCount(count));
  }, [
    isLoaded,
    intercomConfig,
    boot,
    onShow,
    onHide,
    onUnreadCountChanged,
    currentUser,
    currentWorkspace,
    firebaseUID,
    intercomJWT,
    appId,
  ]);

  /**
   * Effect to load and initialize Intercom
   */
  useEffect(() => {
    loadIntercom();
  }, [loadIntercom]);

  /**
   * Effect to initialize when loaded and auto-initialize is enabled
   */
  useEffect(() => {
    if (isLoaded && autoInitialize && isActiveIntercomProduction) {
      initialize();
    }
  }, [isLoaded, autoInitialize, initialize]);

  /**
   * Effect to update Intercom configuration when route, data, or JWT changes
   */
  useEffect(() => {
    if (
      isLoaded &&
      intercomConfig !== null &&
      typeof window.Intercom !== 'undefined' &&
      hasBootedRef.current
    ) {
      // If user is authenticated and we have JWT, update with user_id and JWT
      if (currentUser && currentWorkspace && firebaseUID && intercomJWT) {
        update({
          ...intercomConfig,
          user_id: firebaseUID,
          intercom_user_jwt: intercomJWT,
        });
        setIsIdentified(true);
      } else if (
        currentUser &&
        currentWorkspace &&
        !intercomJWT &&
        !isJWTLoading
      ) {
        // User is authenticated but JWT is not available yet (still loading)
        // Don't update yet, wait for JWT
        return;
      } else {
        // Anonymous mode or user logged out: update to anonymous config
        update({
          app_id: appId,
        });
        setIsIdentified(false);
      }
    }
  }, [
    isLoaded,
    intercomConfig,
    update,
    currentUser,
    currentWorkspace,
    firebaseUID,
    intercomJWT,
    isJWTLoading,
    appId,
  ]);

  /**
   * Cleanup effect - shutdown Intercom and reset state on unmount
   */
  useEffect(() => {
    return () => {
      if (typeof window.Intercom !== 'undefined') {
        shutdown();
      }
      hasBootedRef.current = false;
    };
  }, [shutdown]);

  const methods: tIntercomMethods = {
    boot,
    shutdown,
    update,
    hide,
    show,
    showMessages,
    showNewMessage,
    startConversation,
    trackEvent,
    getVisitorId,
    startTour,
    onHide,
    onShow,
    onUnreadCountChanged,
  };

  return {
    isLoaded,
    isVisible,
    unreadCount,
    isIdentified,
    methods,
  };
}
